import React, { useState, useEffect } from 'react';
import axios from '../../api/axiosInstance';
import CompanyLocationSelector from '../../Components/CompanyLocationSelector';
import Sidebar from '../../Components/Sidebar';
import trident from '../../assets/odin-trident.png';
import './AdminPages.css';
import { getUserRoles, logout } from '../../auth/cookieUtils';
import { apiFileUrl } from '../../config/runtimeConfig';

const CompanyManagement = () => {
  const isSuperAdmin = getUserRoles().includes('SUPER_ADMIN');
  const [companies, setCompanies] = useState([]);
  const [expandedCompanies, setExpandedCompanies] = useState([]);
  const [expandedLocations, setExpandedLocations] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [locationInputs, setLocationInputs] = useState({});
  const [areaInputs, setAreaInputs] = useState({});
  const [locationsByCompany, setLocationsByCompany] = useState({});
  const [areasByLocation, setAreasByLocation] = useState({});
  const [siteFileInputs, setSiteFileInputs] = useState({});
const [uploadingSiteFile, setUploadingSiteFile] = useState({});
const [siteFilesByLocation, setSiteFilesByLocation] = useState({});
const [loadingSiteFiles, setLoadingSiteFiles] = useState({});
const [editingAreaId, setEditingAreaId] = useState(null);
const [editingAreaName, setEditingAreaName] = useState('');
const [updatingBoilerModule, setUpdatingBoilerModule] = useState({});

  useEffect(() => {
    fetchCompanies();
  }, []);


  const fetchCompanies = () => {
    axios.get('/api/companies/all')
      .then(res => setCompanies(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error('Error fetching companies', err);
        alert('Failed to load companies.');
      });
  };

  const fetchLocations = (companyId) => {
    axios.get(`/api/locations/company/${companyId}`)
      .then(res => setLocationsByCompany(prev => ({ ...prev, [companyId]: Array.isArray(res.data) ? res.data : [] })))
      .catch(err => {
        console.error('Error fetching locations', err);
        alert('Failed to load company locations.');
      });
  };

  const fetchAreas = (locationId) => {
    axios.get(`/api/areas/location/${locationId}`)
      .then(res => setAreasByLocation(prev => ({ ...prev, [locationId]: Array.isArray(res.data) ? res.data : [] })))
      .catch(err => {
        console.error('Error fetching areas', err);
        alert('Failed to load location areas.');
      });
  };

  const handleBoilerModuleToggle = async (companyId, location) => {
    if (!isSuperAdmin || updatingBoilerModule[location.id]) return;

    const enabled = !Boolean(location.boilerModuleEnabled);
    try {
      setUpdatingBoilerModule(prev => ({ ...prev, [location.id]: true }));
      const response = await axios.patch(
        `/api/locations/${location.id}/boiler-module?enabled=${enabled}`
      );
      const updatedLocation = response.data;

      setLocationsByCompany(prev => ({
        ...prev,
        [companyId]: (prev[companyId] || []).map(item =>
          item.id === location.id ? updatedLocation : item
        )
      }));

      if (String(location.id) === sessionStorage.getItem('selectedLocationId')) {
        sessionStorage.setItem(
          'selectedLocationBoilerModuleEnabled',
          String(Boolean(updatedLocation.boilerModuleEnabled))
        );
        window.dispatchEvent(new CustomEvent('odin:location-context-changed', {
          detail: {
            locationId: location.id,
            boilerModuleEnabled: Boolean(updatedLocation.boilerModuleEnabled)
          }
        }));
      }
    } catch (err) {
      console.error('Error updating Boiler Matrix module access:', err);
      alert('Failed to update Boiler Matrix module access.');
    } finally {
      setUpdatingBoilerModule(prev => ({ ...prev, [location.id]: false }));
    }
  };

  const handleCreateCompany = () => {
    if (!companyName.trim()) return;
    axios.post('/api/companies', { name: companyName.trim() })
      .then(() => {
        setCompanyName('');
        fetchCompanies();
      })
      .catch((err) => {
        console.error('Error creating company:', err);
        alert('Failed to create company.');
      });
  };

  const handleCreateLocation = (companyId) => {
    const input = locationInputs[companyId];
    if (!input?.name?.trim()) return;

    axios.post(`/api/locations?companyId=${companyId}`, {
      name: input.name.trim(),
      active: true,
      streetAddress: input.streetAddress || '',
      city: input.city || '',
      state: input.state || '',
      zipcode: input.zipcode || '',
      country: input.country || ''
    }).then(() => {
      setLocationInputs(prev => ({ ...prev, [companyId]: {} }));
      fetchLocations(companyId);
    }).catch((err) => {
      console.error('Error creating location:', err);
      alert('Failed to create location.');
    });
  };

  const handleCreateArea = (locationId) => {
    const name = areaInputs[locationId];
    if (!name?.trim()) return;

    axios.post(`/api/areas/location/${locationId}`, { name: name.trim() })
      .then(() => {
        setAreaInputs(prev => ({ ...prev, [locationId]: '' }));
        fetchAreas(locationId);
      })
      .catch((err) => {
        console.error('Error creating area:', err);
        alert('Failed to create area.');
      });
  };

  const toggleCompanyExpand = (companyId) => {
    const isExpanded = expandedCompanies.includes(companyId);
    if (!isExpanded) fetchLocations(companyId);
    setExpandedCompanies(prev =>
      isExpanded ? prev.filter(id => id !== companyId) : [...prev, companyId]
    );
  };

  const toggleLocationExpand = (locationId) => {
  const isExpanded = expandedLocations.includes(locationId);

  if (!isExpanded) {
    fetchAreas(locationId);
    fetchSiteFiles(locationId);
  }

  setExpandedLocations(prev =>
    isExpanded ? prev.filter(id => id !== locationId) : [...prev, locationId]
  );
};

  const updateSiteFileInput = (locationId, field, value) => {
  setSiteFileInputs(prev => ({
    ...prev,
    [locationId]: {
      ...prev[locationId],
      [field]: value
    }
  }));
};

const handleSiteFileUpload = async (locationId) => {
  const input = siteFileInputs[locationId];

  if (!input?.section) {
    alert('Please select a file type.');
    return;
  }

  if (!input?.file) {
    alert('Please choose a file to upload.');
    return;
  }

  const formData = new FormData();
  formData.append('file', input.file);

  if (input.displayName?.trim()) {
    formData.append('displayName', input.displayName.trim());
  }

  try {
    setUploadingSiteFile(prev => ({ ...prev, [locationId]: true }));

    await axios.post(
      `/api/files/upload/${input.section}?locationId=${locationId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      }
    );

    alert('Site file uploaded successfully.');

    await fetchSiteFiles(locationId);

    setSiteFileInputs(prev => ({
      ...prev,
      [locationId]: {
        section: '',
        displayName: '',
        file: null
      }
    }));

    const fileInput = document.getElementById(`site-file-upload-${locationId}`);
    if (fileInput) fileInput.value = '';

  } catch (err) {
    console.error('Error uploading site file:', err);
    alert('Failed to upload site file.');
  } finally {
    setUploadingSiteFile(prev => ({ ...prev, [locationId]: false }));
  }
};

const resolveFileUrl = apiFileUrl;

const fetchSiteFiles = async (locationId) => {
  try {
    setLoadingSiteFiles(prev => ({ ...prev, [locationId]: true }));

    const res = await axios.get(`/api/files/location/${locationId}/site-files`);

    setSiteFilesByLocation(prev => ({
      ...prev,
      [locationId]: res.data || {
        siteMapAerialPhotoDocuments: [],
        guidelineProcedureDocuments: []
      }
    }));
  } catch (err) {
    console.error('Error fetching site files:', err);
    setSiteFilesByLocation(prev => ({
      ...prev,
      [locationId]: {
        siteMapAerialPhotoDocuments: [],
        guidelineProcedureDocuments: []
      }
    }));
  } finally {
    setLoadingSiteFiles(prev => ({ ...prev, [locationId]: false }));
  }
};

const handleDeleteSiteFile = async (locationId, section, fileId) => {
  const confirmed = window.confirm('Are you sure you want to delete this site file?');
  if (!confirmed) return;

  try {
    await axios.delete(`/api/files/delete/${section}/${fileId}`);
    await fetchSiteFiles(locationId);
  } catch (err) {
    console.error('Error deleting site file:', err);
    alert('Failed to delete site file.');
  }
};

const handleUpdateArea = async (locationId, areaId) => {
  const trimmedName = editingAreaName.trim();

  if (!trimmedName) {
    alert('Area name cannot be blank.');
    return;
  }

  try {
    await axios.put(`/api/areas/${areaId}`, {
      id: areaId,
      name: trimmedName
    });

    setEditingAreaId(null);
    setEditingAreaName('');
    fetchAreas(locationId);
  } catch (err) {
    console.error('Error updating area:', err);
    alert('Failed to update area.');
  }
};

  const handleLogout = () => {
    logout();
  };

  const siteFileTypeOptions = [
  {
    label: 'Site Map / Aerial Photo',
    value: 'siteMapAerialPhotoDocuments'
  },
  {
    label: 'Guideline / Procedure',
    value: 'guidelineProcedureDocuments'
  }
];

  return (
    <div className="dashboard-page">
      <div className="dashboard-topbar">
        <div className="topbar-left">
          <img src={trident} alt="Odin Logo" className="navbar-logo" />
          <CompanyLocationSelector />
        </div>

        <div className="topbar-center">
          <div className="page-title">Company Management</div>
        </div>

        <div className="topbar-right">
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-main">
        <Sidebar />

        <div className="dashboard-content">
          <div style={{ marginBottom: '24px' }}>
            <h3>Add New Company</h3>
            <input
              type="text"
              placeholder="Company Name"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
            />
            <button onClick={handleCreateCompany}>Create Company</button>
          </div>

          {companies.map(company => (
            <div key={company.id} className="company-block">
              <div
                className="company-header"
                onClick={() => toggleCompanyExpand(company.id)}
              >
                {company.name} {expandedCompanies.includes(company.id) ? '▼' : '▶'}
              </div>

              {expandedCompanies.includes(company.id) && (
                <>
                  <div className="add-form">
                    <h4>Add Location</h4>
                    <input
                      type="text"
                      placeholder="Location Name"
                      value={locationInputs[company.id]?.name || ''}
                      onChange={e =>
                        setLocationInputs(prev => ({
                          ...prev,
                          [company.id]: {
                            ...prev[company.id],
                            name: e.target.value
                          }
                        }))
                      }
                    />
                    <button onClick={() => handleCreateLocation(company.id)}>Add</button>
                  </div>

                  {(locationsByCompany[company.id] || []).map(location => (
                    <div key={location.id} className="location-item">
                     <div
  className="location-toggle"
  onClick={() => toggleLocationExpand(location.id)}
>
  {location.name} {expandedLocations.includes(location.id) ? '▼' : '▶'}
</div>

                      {expandedLocations.includes(location.id) && (
  <>
    {isSuperAdmin && (
      <div className="boiler-module-control">
        <div>
          <strong>Boiler Matrix module</strong>
          <span>Separately billed location feature. Disabling access preserves all boiler history.</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={Boolean(location.boilerModuleEnabled)}
          className={`boiler-module-toggle ${location.boilerModuleEnabled ? 'enabled' : 'disabled'}`}
          disabled={Boolean(updatingBoilerModule[location.id])}
          onClick={() => handleBoilerModuleToggle(company.id, location)}
        >
          {updatingBoilerModule[location.id]
            ? 'Saving...'
            : location.boilerModuleEnabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>
    )}
    <div className="location-details-grid">
    <div className="location-areas-panel">
      <div className="location-panel-header">
        <h5>Areas</h5>
        <span>{(areasByLocation[location.id] || []).length} total</span>
      </div>

      <div className="area-scroll-list">
  <ul>
    {(areasByLocation[location.id] || []).map(area => (
      <li key={area.id} className="area-management-row">
        {editingAreaId === area.id ? (
          <>
            <input
              type="text"
              value={editingAreaName}
              onChange={(e) => setEditingAreaName(e.target.value)}
              className="area-edit-input"
            />

            <div className="area-actions">
              <button
                className="area-save-btn"
                onClick={() => handleUpdateArea(location.id, area.id)}
              >
                Save
              </button>

              <button
                className="area-cancel-btn"
                onClick={() => {
                  setEditingAreaId(null);
                  setEditingAreaName('');
                }}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="area-name-text">{area.name}</span>

            <div className="area-actions">
              <button
                className="area-edit-btn"
                onClick={() => {
                  setEditingAreaId(area.id);
                  setEditingAreaName(area.name);
                }}
              >
                Rename
              </button>
            </div>
          </>
        )}
      </li>
    ))}
  </ul>
</div>

      <div className="area-add-row">
        <input
          type="text"
          placeholder="New Area Name"
          value={areaInputs[location.id] || ''}
          onChange={e =>
            setAreaInputs(prev => ({
              ...prev,
              [location.id]: e.target.value
            }))
          }
        />
        <button onClick={() => handleCreateArea(location.id)}>
          Add Area
        </button>
      </div>
    </div>

    <div className="site-file-upload-section">
      <h5>Site Files</h5>

      <div className="site-file-upload-grid">
        <div className="form-group">
          <label>File Type</label>
          <select
            value={siteFileInputs[location.id]?.section || ''}
            onChange={e => updateSiteFileInput(location.id, 'section', e.target.value)}
          >
            <option value="">Select File Type</option>
            {siteFileTypeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Display Name</label>
          <input
            type="text"
            placeholder="Example: Main Site Map"
            value={siteFileInputs[location.id]?.displayName || ''}
            onChange={e => updateSiteFileInput(location.id, 'displayName', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>File</label>
          <input
            id={`site-file-upload-${location.id}`}
            type="file"
            onChange={e =>
              updateSiteFileInput(location.id, 'file', e.target.files?.[0] || null)
            }
          />
        </div>
      </div>

      <button
        onClick={() => handleSiteFileUpload(location.id)}
        disabled={uploadingSiteFile[location.id]}
      >
        {uploadingSiteFile[location.id] ? 'Uploading...' : 'Upload Site File'}
      </button>
      <div className="uploaded-site-files">
  <h5>Uploaded Site Files</h5>

  {loadingSiteFiles[location.id] ? (
    <p className="site-file-empty">Loading files...</p>
  ) : (
    <>
      <div className="site-file-list-group">
        <h6>Site Maps / Aerial Photos</h6>

        {(siteFilesByLocation[location.id]?.siteMapAerialPhotoDocuments || []).length > 0 ? (
          <ul>
            {(siteFilesByLocation[location.id]?.siteMapAerialPhotoDocuments || []).map(file => (
              <li key={`site-map-${file.id}`}>
                <a
                  href={resolveFileUrl(file.filePath)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {file.displayName?.trim() || file.fileName || 'Unnamed File'}
                </a>

                <button
                  className="delete-site-file-btn"
                  onClick={() =>
                    handleDeleteSiteFile(location.id, 'siteMapAerialPhotoDocuments', file.id)
                  }
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="site-file-empty">No site maps or aerial photos uploaded.</p>
        )}
      </div>

      <div className="site-file-list-group">
        <h6>Guidelines / Procedures</h6>

        {(siteFilesByLocation[location.id]?.guidelineProcedureDocuments || []).length > 0 ? (
          <ul>
            {(siteFilesByLocation[location.id]?.guidelineProcedureDocuments || []).map(file => (
              <li key={`guideline-${file.id}`}>
                <a
                  href={resolveFileUrl(file.filePath)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {file.displayName?.trim() || file.fileName || 'Unnamed File'}
                </a>

                <button
                  className="delete-site-file-btn"
                  onClick={() =>
                    handleDeleteSiteFile(location.id, 'guidelineProcedureDocuments', file.id)
                  }
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="site-file-empty">No guidelines or procedures uploaded.</p>
        )}
      </div>
    </>
  )}
</div>
    </div>
  </div>
  </>
)}
                      
                    </div>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompanyManagement;
